import React, { memo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styled, { css } from 'styled-components';

import { careerColor } from 'services/careerTokens';
import { careerLayout, careerType } from 'services/careerType';

export const SKILL_LEVELS = [1, 2, 3, 4, 5] as const;

// React 17: `useId` ещё нет — имя группы переключателей из счётчика модуля.
let groupCounter = 0;

/** Название и описание уровня — литералами: их видит сканер паритета локалей. */
export const SKILL_LEVEL_TEXT: Record<number, { title: string; desc: string }> = {
  1: { title: 'careerCenter.skillLevel.1.title', desc: 'careerCenter.skillLevel.1.desc' },
  2: { title: 'careerCenter.skillLevel.2.title', desc: 'careerCenter.skillLevel.2.desc' },
  3: { title: 'careerCenter.skillLevel.3.title', desc: 'careerCenter.skillLevel.3.desc' },
  4: { title: 'careerCenter.skillLevel.4.title', desc: 'careerCenter.skillLevel.4.desc' },
  5: { title: 'careerCenter.skillLevel.5.title', desc: 'careerCenter.skillLevel.5.desc' },
};

export interface ISkillLevelChoice {
  /** Имя навыка — для доступного имени группы: «Poziom: Celery». */
  skillName: string;
  value: number | null;
  onChange: (level: number) => void;
  /**
   * `compact` — строка редактора CV: пять кнопок-цифр и описание выбранного
   * (или того, что под курсором/фокусом) под ними.
   * `full` — паспорт навыка: пять карточек с названием и описанием сразу.
   */
  variant?: 'compact' | 'full';
  disabled?: boolean;
  /** Приписка в строке под цифрами — источник оценки: «Twoja ocena». */
  hint?: string;
}

/**
 * Самооценка уровня навыка (0072, шаг 3) — вместо звёзд antd `Rate`.
 *
 * Аудит: пять безымянных звёзд читались «star star star», а что значит
 * «три звезды», нигде не было написано. Теперь у каждого уровня номер,
 * название и описание, и описание видно ДО выбора: оценивают по словам, а не
 * по количеству звёзд.
 *
 * Разметка — `radiogroup` с настоящими `radio`: стрелки и Tab работают как у
 * нативной группы переключателей.
 */
export const SkillLevelChoice = memo(
  ({
    skillName,
    value,
    onChange,
    variant = 'compact',
    disabled = false,
    hint,
  }: ISkillLevelChoice) => {
    const { t } = useTranslation();
    const id = useRef(`skill-level-${(groupCounter += 1)}`).current;
    const [preview, setPreview] = useState<number | null>(null);
    const shown = preview ?? value;

    if (variant === 'full') {
      return (
        <FullGroup
          role="radiogroup"
          aria-label={t('careerCenter.skillLevel.label', { skill: skillName })}>
          {SKILL_LEVELS.map((level) => (
            <FullOption key={level} $checked={value === level}>
              <input
                type="radio"
                name={id}
                value={level}
                checked={value === level}
                disabled={disabled}
                onChange={() => onChange(level)}
              />
              <span>
                <FullTitle>
                  {level} · {t(SKILL_LEVEL_TEXT[level].title)}
                </FullTitle>
                <Desc>{t(SKILL_LEVEL_TEXT[level].desc)}</Desc>
              </span>
            </FullOption>
          ))}
        </FullGroup>
      );
    }

    return (
      <Compact>
        <Digits
          role="radiogroup"
          aria-label={t('careerCenter.skillLevel.label', { skill: skillName })}
          onMouseLeave={() => setPreview(null)}>
          {SKILL_LEVELS.map((level) => (
            <Digit key={level} $checked={value === level} $disabled={disabled}>
              <input
                type="radio"
                name={id}
                value={level}
                checked={value === level}
                disabled={disabled}
                aria-label={`${level} · ${t(SKILL_LEVEL_TEXT[level].title)}`}
                aria-describedby={`${id}-desc-${level}`}
                onChange={() => onChange(level)}
                onFocus={() => setPreview(level)}
                onBlur={() => setPreview(null)}
              />
              <span aria-hidden onMouseEnter={() => setPreview(level)}>
                {level}
              </span>
              <Hidden id={`${id}-desc-${level}`}>{t(SKILL_LEVEL_TEXT[level].desc)}</Hidden>
            </Digit>
          ))}
        </Digits>
        {/* В списке из пятнадцати навыков описание под каждой строкой делало
            форму втрое длиннее. Название выбранного уровня видно всегда,
            описание — у того уровня, что под курсором или в фокусе; читалке
            описание каждого уровня привязано через aria-describedby. */}
        <Shown aria-hidden>
          {preview ? (
            <>
              <ShownTitle>{t(SKILL_LEVEL_TEXT[preview].title)}</ShownTitle>
              {' — '}
              {t(SKILL_LEVEL_TEXT[preview].desc)}
            </>
          ) : shown ? (
            <ShownTitle>{t(SKILL_LEVEL_TEXT[shown].title)}</ShownTitle>
          ) : (
            t('careerCenter.skillLevel.none')
          )}
          {hint ? <> · {hint}</> : null}
        </Shown>
      </Compact>
    );
  },
);

const Compact = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const Digits = styled.div`
  display: inline-flex;
  gap: 4px;
`;

const Digit = styled.label<{ $checked: boolean; $disabled: boolean }>`
  position: relative;
  display: inline-flex;

  input {
    position: absolute;
    inset: 0;
    margin: 0;
    opacity: 0;
    cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  }

  span:not([id]) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: ${careerLayout.hitTarget}px;
    height: ${careerLayout.hitTarget}px;
    border: 1px solid ${careerColor.line};
    border-radius: ${careerLayout.controlRadius}px;
    color: ${careerColor.text};
    ${careerType.bodyStrong()};

    ${({ $checked }) =>
      $checked &&
      css`
        border-color: ${careerColor.text};
        background: ${careerColor.active};
      `}
  }

  input:focus-visible + span {
    outline: 2px solid ${careerColor.text};
    outline-offset: 2px;
  }
`;

const Hidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

const Shown = styled.p`
  margin: 0;
  color: ${careerColor.text2};
  ${careerType.meta()};
`;

const ShownTitle = styled.span`
  color: ${careerColor.text};
`;

const FullGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FullOption = styled.label<{ $checked: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-height: ${careerLayout.hitTarget}px;
  padding: 10px 12px;
  border: 1px solid ${({ $checked }) => ($checked ? careerColor.text : careerColor.line)};
  border-radius: ${careerLayout.controlRadius}px;
  background: ${({ $checked }) => ($checked ? careerColor.active : 'transparent')};
  cursor: pointer;

  input {
    margin-top: 4px;
    accent-color: ${careerColor.text};
  }
`;

const FullTitle = styled.span`
  display: block;
  color: ${careerColor.text};
  ${careerType.bodyStrong()};
`;

const Desc = styled.span`
  display: block;
  color: ${careerColor.text2};
  ${careerType.meta()};
`;
